import { memo } from 'react';
import { Box, Typography, Tooltip, Fab } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PreviewIcon          from '@mui/icons-material/Visibility';
import SaveIcon             from '@mui/icons-material/Save';
import ErrorOutlineIcon     from '@mui/icons-material/ErrorOutline';
import SettingsIcon         from '@mui/icons-material/Settings';
{/*menu flotante a la derecha */}
const FloatingActionButtons = memo(({
  onAddQuestion, onPreview, onSave, onSettings, disabledPreview, disableAdd, isSaveDisabled,
  hasValidationError, t,
}) => (
  <Box sx={{ position: 'fixed', right: 24, bottom: '50%', transform: 'translateY(50%)', display: 'flex', flexDirection: 'column', gap: 1.5, zIndex: 1050 }}>

    {/* ── Agregar pregunta ── */}
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={disableAdd ? t.addDisabledTooltip : t.addQuestionTooltip} placement="left" arrow>
        <span>
          <Fab
            color="primary"
            onClick={onAddQuestion}
            aria-label={t.addQuestionTooltip}
            disabled={disableAdd}
            size="medium"
            sx={{
              boxShadow: '0 4px 12px rgba(25,118,210,0.35)',
              transition: 'transform 0.15s, box-shadow 0.15s',
              '&:not(:disabled):hover': { transform: 'scale(1.08)', boxShadow: '0 6px 16px rgba(25,118,210,0.45)' },
            }}
          >
            <AddCircleOutlineIcon />
          </Fab>
        </span>
      </Tooltip>
      <Typography variant="caption" sx={{ color: disableAdd ? 'text.disabled' : 'text.secondary', fontSize: '0.65rem', fontWeight: 500 }}>
        {t.fabAddLabel}
      </Typography>
    </Box>

    {/* ── Vista previa ── */}
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={t.previewTooltip} placement="left" arrow>
        <span>
          <Fab
            color="secondary"
            onClick={onPreview}
            aria-label={t.previewTooltip}
            disabled={disabledPreview}
            size="medium"
            sx={{
              boxShadow: '0 4px 12px rgba(156,39,176,0.3)',
              transition: 'transform 0.15s, box-shadow 0.15s',
              '&:not(:disabled):hover': { transform: 'scale(1.08)', boxShadow: '0 6px 16px rgba(156,39,176,0.4)' },
            }}
          >
            <PreviewIcon />
          </Fab>
        </span>
      </Tooltip>
      <Typography variant="caption" sx={{ color: disabledPreview ? 'text.disabled' : 'text.secondary', fontSize: '0.65rem', fontWeight: 500 }}>
        {t.fabPreviewLabel}
      </Typography>
    </Box>

    {/* ── Guardar ── */}
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      <Tooltip
        title={hasValidationError ? (t.validationSaveTooltip ?? 'Hay campos sin completar') : t.fabSaveTooltip}
        placement="left"
        arrow
      >
        <span style={{ position: 'relative', display: 'inline-flex' }}>
          <Fab
            color="primary"
            onClick={onSave}
            aria-label={t.fabSaveAriaLabel}
            disabled={isSaveDisabled}
            size="medium"
            sx={{
              boxShadow: hasValidationError
                ? '0 4px 12px rgba(211,47,47,0.5)'
                : '0 4px 12px rgba(25,118,210,0.35)',
              bgcolor: isSaveDisabled ? undefined : hasValidationError ? 'error.main' : 'success.main',
              '&:not(:disabled)': { bgcolor: hasValidationError ? 'error.main' : 'success.main' },
              '&:not(:disabled):hover': {
                bgcolor: hasValidationError ? 'error.dark' : 'success.dark',
                transform: 'scale(1.08)',
                boxShadow: hasValidationError
                  ? '0 6px 16px rgba(211,47,47,0.55)'
                  : '0 6px 16px rgba(46,125,50,0.45)',
              },
              transition: 'transform 0.15s, box-shadow 0.15s, background-color 0.2s',
              animation: hasValidationError && !isSaveDisabled ? 'unsavedPulse 1.4s ease-in-out infinite' : 'none',
            }}
          >
            {hasValidationError ? <ErrorOutlineIcon /> : <SaveIcon />}
          </Fab>

          {/* Badge de alerta */}
          {hasValidationError && !isSaveDisabled && (
            <Box
              sx={{
                position: 'absolute', top: -4, right: -4,
                width: 18, height: 18, borderRadius: '50%',
                bgcolor: 'warning.main', border: '2px solid white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'floatIcon 1.2s ease-in-out infinite',
                pointerEvents: 'none',
              }}
            >
              <Typography sx={{ fontSize: '0.55rem', fontWeight: 900, color: 'white', lineHeight: 1 }}>!</Typography>
            </Box>
          )}
        </span>
      </Tooltip>
      <Typography
        variant="caption"
        sx={{
          color: isSaveDisabled ? 'text.disabled' : hasValidationError ? 'error.main' : 'success.main',
          fontSize: '0.65rem',
          fontWeight: 500,
        }}
      >
        {hasValidationError ? (t.fabSaveErrorLabel ?? 'Revisar') : t.fabSaveLabel}
      </Typography>
    </Box>

    {/* ── Configuración ── */}
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={t.settingsButtonTooltip} placement="left" arrow>
        <Fab
          onClick={onSettings}
          aria-label={t.settingsButtonAriaLabel}
          size="medium"
          sx={{
            boxShadow: '0 4px 12px rgba(25,118,210,0.25)',
            bgcolor: 'white', color: 'primary.main',
            border: '2px solid', borderColor: 'primary.200',
            transition: 'transform 0.15s, box-shadow 0.15s, background-color 0.2s',
            '&:hover': {
              bgcolor: 'primary.50', borderColor: 'primary.main',
              transform: 'scale(1.08)', boxShadow: '0 6px 16px rgba(25,118,210,0.35)',
            },
          }}
        >
          <SettingsIcon />
        </Fab>
      </Tooltip>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 500 }}>
        {t.settingsButtonText}
      </Typography>
    </Box>

  </Box>
));

FloatingActionButtons.displayName = 'FloatingActionButtons';

export default FloatingActionButtons;